import { Button, Field, FieldInput, FieldLabel, FieldSelect } from '@ibe/ui';
import { type QueryKey } from '@tanstack/react-query';
import { z } from 'zod';

import { useApiQuery } from '../../lib/api/hooks.js';
import { type FilterDescriptor, activeFilterValues } from '../../lib/resource-list/filters.js';

/**
 * The filter controls above a list.
 *
 * The controls come from `@ibe/ui` rather than being raw elements, so a filter
 * row looks and behaves exactly like a form field elsewhere in the portal. A
 * native `<select>` is keyboard-correct for free but draws its option list
 * with the operating system, which is the one moment the palette and focus
 * ring defined in `styles.css` stop applying.
 */

/**
 * The value standing in for "no filter".
 *
 * A dropdown needs a selectable entry to clear itself, and Radix reserves the
 * empty string for "nothing chosen" — an item carrying it throws. So the
 * "Any" entry gets a sentinel, translated back to `undefined` on the way out.
 * Without this the only way to clear one filter is to clear all of them.
 */
const ANY = '__any__';

const entityOptionSchema = z.array(z.object({ value: z.string(), label: z.string() }));

export interface FilterBarProps {
  readonly filters: readonly FilterDescriptor[];
  readonly params: URLSearchParams;
  /** Applies a change. Undefined clears the filter. */
  readonly onChange: (param: string, value: string | undefined) => void;
  /** Clears all filters in one update. If omitted, onChange is called per filter. */
  readonly onClear?: () => void;
}

interface EntityFilterSelectProps {
  readonly path: string | undefined;
  readonly value: string;
  readonly onValueChange: (value: string | undefined) => void;
}

function EntityFilterSelect({
  path,
  value,
  onValueChange,
}: EntityFilterSelectProps): React.JSX.Element {
  const queryKey: QueryKey = ['filter-options', path ?? ''];
  const query = useApiQuery({
    queryKey,
    path: path ?? '',
    schema: entityOptionSchema,
    enabled: path !== undefined && path !== '',
  });

  const options = query.data ?? [];

  return (
    <FieldSelect
      value={value}
      onValueChange={(next) => {
        onValueChange(next === ANY ? undefined : next);
      }}
      options={[{ value: ANY, label: 'Any' }, ...options]}
      disabled={query.isPending}
    />
  );
}

export function FilterBar({
  filters,
  params,
  onChange,
  onClear,
}: FilterBarProps): React.JSX.Element | null {
  if (filters.length === 0) return null;

  const active = activeFilterValues(filters, params);
  const anyActive = Object.keys(active).length > 0;

  return (
    <div className="flex flex-wrap items-end gap-field">
      {filters.map((filter) => (
        <div key={filter.id} className="min-w-44">
          <Field>
            <FieldLabel>{filter.label}</FieldLabel>
            {filter.kind === 'entity' ? (
              <EntityFilterSelect
                path={filter.optionsPath}
                value={active[filter.param] ?? ANY}
                onValueChange={(value) => {
                  onChange(filter.param, value);
                }}
              />
            ) : filter.kind === 'text' || filter.kind === 'date' ? (
              <FieldInput
                type={filter.kind}
                value={active[filter.param] ?? ''}
                onChange={(event) => {
                  const value = event.currentTarget.value;
                  onChange(filter.param, value === '' ? undefined : value);
                }}
              />
            ) : (
              <FieldSelect
                value={active[filter.param] ?? ANY}
                onValueChange={(value) => {
                  onChange(filter.param, value === ANY ? undefined : value);
                }}
                options={[
                  { value: ANY, label: 'Any' },
                  ...(filter.kind === 'boolean'
                    ? [
                        { value: 'true', label: 'Yes' },
                        { value: 'false', label: 'No' },
                      ]
                    : (filter.options ?? [])),
                ]}
              />
            )}
          </Field>
        </div>
      ))}

      {!anyActive ? null : (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => {
            if (onClear !== undefined) {
              onClear();
            } else {
              for (const filter of filters) onChange(filter.param, undefined);
            }
          }}
        >
          Clear filters
        </Button>
      )}
    </div>
  );
}
